import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { UserContext } from "../../../context/UserContext";

const SIDEBAR_BG = "#f8fafc";
const BORDER_COLOR = "#e2e8f0";
const ACTIVE_BG = "rgba(79, 70, 229, 0.08)";
const ACTIVE_COLOR = "#4f46e5";
const TEXT_COLOR = "#334155";
const TEXT_MUTED = "#64748b";
const HOVER_BG = "rgba(148, 163, 184, 0.08)";

const SideMenuList = ({ menuList, onLogout }) => {
  const location = useLocation();
  const theme = useTheme();
  const { user, loading } = useContext(UserContext);
  const [openSubmenus, setOpenSubmenus] = useState({});

  const handleToggleSubmenu = (text) => {
    setOpenSubmenus((prev) => ({ ...prev, [text]: !prev[text] }));
  };

  const isActive = (to) => {
    if (!to) return false;
    if (to === "/exhibitor") return location.pathname === "/exhibitor";
    return location.pathname.startsWith(to);
  };

  return (
    <Box
      sx={{
        width: 240,
        minHeight: "calc(100vh - 86px)",
        background: SIDEBAR_BG,
        borderRight: `1px solid ${BORDER_COLOR}`,
        display: "flex",
        flexDirection: "column",
        py: 2.5,
        overflow: "hidden",
      }}
    >
      {/* <Box sx={{ px: 2.5, pb: 2 }}>
        <Typography
          variant="overline"
          sx={{
            fontSize: "0.65rem",
            letterSpacing: 1.5,
            color: TEXT_MUTED,
            fontWeight: 800,
            display: "block",
          }}
        >
          {user.role.charAt(0).toUpperCase() + user.role.slice(1)} Console
        </Typography>
      </Box> */}

      <Divider sx={{ borderColor: BORDER_COLOR, mb: 1.5 }} />

      <List dense disablePadding sx={{ px: 1.5, flexGrow: 1 }}>
        {menuList.map((item, idx) => {
          const Icon = item.icon;
          const active = isActive(item.to);

          if (item.sublinks) {
            const anyChildActive = item.sublinks.some((sl) =>
              location.pathname.startsWith(sl.link)
            );
            const isSubmenuOpen = openSubmenus[item.text] ?? anyChildActive;

            return (
              <Box key={idx} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleToggleSubmenu(item.text)}
                  sx={{
                    borderRadius: 2.5,
                    py: 1.2,
                    px: 1.5,
                    backgroundColor: anyChildActive
                      ? alpha(ACTIVE_COLOR, 0.04)
                      : "transparent",
                    "&:hover": {
                      backgroundColor: HOVER_BG,
                      "& .MuiListItemIcon-root": { color: ACTIVE_COLOR },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: anyChildActive ? ACTIVE_COLOR : TEXT_MUTED,
                      transition: "color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: "0.85rem",
                      fontWeight: anyChildActive ? 700 : 600,
                      color: anyChildActive ? ACTIVE_COLOR : TEXT_COLOR,
                    }}
                  />
                  {isSubmenuOpen ? (
                    <ExpandLessIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />
                  )}
                </ListItemButton>

                <Collapse in={isSubmenuOpen} timeout="auto" unmountOnExit>
                  <List
                    disablePadding
                    sx={{ pl: 2, mt: 0.5, position: "relative" }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: 8,
                        top: 0,
                        bottom: 0,
                        width: "1px",
                        backgroundColor: BORDER_COLOR,
                      }}
                    />

                    {item.sublinks.map((sl, si) => {
                      const slActive = location.pathname.startsWith(sl.link);
                      return (
                        <ListItemButton
                          key={si}
                          component={Link}
                          to={sl.link}
                          sx={{
                            borderRadius: 2,
                            py: 0.8,
                            px: 1.5,
                            mb: 0.3,
                            backgroundColor: slActive
                              ? ACTIVE_BG
                              : "transparent",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              backgroundColor: slActive ? ACTIVE_BG : HOVER_BG,
                              "& .submenu-arrow": {
                                transform: "translateX(2px)",
                                color: ACTIVE_COLOR,
                              },
                            },
                          }}
                        >
                          <ChevronRightIcon
                            className="submenu-arrow"
                            sx={{
                              fontSize: 12,
                              color: slActive ? ACTIVE_COLOR : TEXT_MUTED,
                              mr: 1,
                              transition: "all 0.2s ease",
                            }}
                          />
                          <ListItemText
                            primary={sl.label}
                            primaryTypographyProps={{
                              fontSize: "0.8rem",
                              fontWeight: slActive ? 700 : 500,
                              color: slActive ? ACTIVE_COLOR : TEXT_MUTED,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          return (
            <Tooltip key={idx} title="" placement="right">
              <ListItemButton
                component={item.to ? Link : "div"}
                to={item.to || undefined}
                selected={active}
                aria-current={active ? "page" : undefined}
                onClick={!item.to ? onLogout : undefined}
                sx={{
                  borderRadius: 2.5,
                  mb: 0.5,
                  py: 1.2,
                  px: 1.5,
                  backgroundColor: active ? ACTIVE_BG : "transparent",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: active ? ACTIVE_BG : HOVER_BG,
                    "& .MuiListItemIcon-root": { color: ACTIVE_COLOR },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: active ? ACTIVE_COLOR : TEXT_MUTED,
                    transition: "color 0.2s ease",
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: "0.85rem",
                    fontWeight: active ? 700 : 600,
                    color: active ? ACTIVE_COLOR : TEXT_COLOR,
                  }}
                />
                {active && (
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      backgroundColor: ACTIVE_COLOR,
                      flexShrink: 0,
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>

      <Divider sx={{ borderColor: BORDER_COLOR, mt: 1.5, mb: 1.5 }} />
      <Box sx={{ px: 1.5 }}>
        <ListItemButton
          onClick={onLogout}
          sx={{
            borderRadius: 2.5,
            py: 1.2,
            px: 1.5,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.08),
              "& .MuiListItemIcon-root": { color: theme.palette.error.main },
              "& .MuiListItemText-primary": { color: theme.palette.error.main },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: TEXT_MUTED }}>
            <LogoutIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: TEXT_COLOR,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );
};

export default SideMenuList;
